let draftSelections = {};
let samplesSyncTimeout = null;
let isRemovingSamplesForThreshold = false;
let isConfirmingSamples = false;
let isRemovingCartItem = false;
let lastConfirmedSampleOrder = [];
let lastPopupTrigger = null;
let cartSamplesInitialized = false;
let currentSelectedSampleIds = [];
let pendingSamplesCommit = null;
const CART_SAMPLES_SYNC_DELAY = 20;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartSamples);
} else {
  initCartSamples();
}

document.addEventListener(
  'click',
  function (event) {
    const openBtn = event.target.closest('[data-open-samples-popup]');
    const closeBtn = event.target.closest('[data-close-samples-popup]');
    const removeBtn = event.target.closest('[data-item-remove]');
    const sampleCard = event.target.closest('[data-sample-card]');
    const clearBtn = event.target.closest('[data-clear-samples]');
    const confirmBtn = event.target.closest('[data-confirm-samples]');

    if (removeBtn && shouldHandleCartRemoval(removeBtn)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      removeCartItem(removeBtn);
      return;
    }

    if (openBtn) {
      const wrapper = openBtn.closest('[data-cart-samples]');
      if (!wrapper || wrapper.dataset.qualifies !== 'true') return;

      const popup = getSamplesPopup(wrapper.dataset.popupId);
      if (!popup) return;

      lastPopupTrigger = openBtn;
      initializeDraftSelections(popup);
      renderDraftSelections(popup);
      openSamplesPopup(popup);
      return;
    }

    if (closeBtn) {
      const popup = closeBtn.closest('[data-samples-popup]');
      if (popup) {
        closeSamplesPopup(popup);
      }
      return;
    }

    if (clearBtn) {
      const popup = clearBtn.closest('[data-samples-popup]');
      if (popup) {
        clearDraftSelections(popup);
      }
      return;
    }

    if (confirmBtn) {
      const popup = confirmBtn.closest('[data-samples-popup]');
      if (popup) {
        confirmDraftSelections(popup);
      }
      return;
    }

    if (sampleCard) {
      const popup = sampleCard.closest('[data-samples-popup]');
      if (popup) {
        toggleDraftSelection(sampleCard, popup);
      }
    }
  },
  true
);

document.addEventListener('keydown', function (event) {
  const card = event.target.closest('[data-sample-card]');

  if (card && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault();

    const popup = card.closest('[data-samples-popup]');
    if (popup) {
      toggleDraftSelection(card, popup);
    }
    return;
  }

  if (event.key === 'Escape') {
    const popup = document.querySelector('[data-samples-popup]:not([hidden])');
    if (popup) {
      closeSamplesPopup(popup);
    }
  }
});

function initCartSamples() {
  if (document.body.dataset.samplesEnabled === 'false') {
    removeSamplesWhenDisabled();
    return;
  }

  moveSamplesPopupsToBody();

  if (!cartSamplesInitialized) {
    bindCartSamplesEvents();
    cartSamplesInitialized = true;
  }

  const initialSelectedIds = getCurrentSelectedIds();
  if (initialSelectedIds.length) {
    lastConfirmedSampleOrder = mergeSampleOrder(initialSelectedIds);
  }

  setPopupSelectionState(initialSelectedIds);
  syncSamplesUiFromDom();
}

async function removeSamplesWhenDisabled() {
  try {
    const cart = await fetchCart();
    const updates = {};

    getSampleItems(cart).forEach(function (item) {
      updates[item.key] = 0;
    });

    if (Object.keys(updates).length) {
      await postCartUpdate({ updates: updates });
      requestNativeCartRefresh();
    }
  } catch (error) {
    console.error('Unable to remove unavailable samples from the cart.', error);
  }
}

function bindCartSamplesEvents() {
  document.addEventListener('theme:cart:change', function () {
    scheduleSamplesSync(0);
  });

  document.addEventListener('theme:cart-drawer:open', function () {
    moveSamplesPopupsToBody();
    scheduleSamplesSync(0);
  });

  document.addEventListener('shopify:section:load', function () {
    moveSamplesPopupsToBody();
    scheduleSamplesSync(0);
  });

  document.addEventListener('submit', function (event) {
    const form = event.target.closest('[data-cart-form]');
    if (!form) return;

    if (!form.hasAttribute('data-samples-submit-ready')) {
      event.preventDefault();

      if (form.hasAttribute('data-samples-submit-pending')) {
        return;
      }

      const submitter = event.submitter && document.contains(event.submitter) ? event.submitter : null;
      form.setAttribute('data-samples-submit-pending', 'true');

      const checkoutGuard = (pendingSamplesCommit || Promise.resolve()).then(enforceSampleEligibilityBeforeCheckout);

      checkoutGuard.then(function () {
        if (!document.contains(form)) return;

        form.removeAttribute('data-samples-submit-pending');
        prepareCartInputsForCheckoutSubmission(form);
        form.setAttribute('data-samples-submit-ready', 'true');

        if (typeof form.requestSubmit === 'function') {
          if (submitter && document.contains(submitter)) {
            form.requestSubmit(submitter);
          } else {
            form.requestSubmit();
          }
        } else {
          form.submit();
        }
      }).catch(function (error) {
        console.error(error);
        form.removeAttribute('data-samples-submit-pending');

        const errorMessage = document.querySelector('[data-cart-errors] [data-error-message]');
        if (errorMessage) {
          errorMessage.textContent = 'We could not verify your sample eligibility. Please refresh your cart and try again.';
          errorMessage.closest('[data-cart-errors]')?.classList.add('is-visible');
        }
      });

      return;
    }

    if (form.hasAttribute('data-samples-submit-ready')) {
      form.removeAttribute('data-samples-submit-ready');
    }

    prepareCartInputsForCheckoutSubmission(form);
  });
}

function getSamplesRoot() {
  return document.querySelector('[data-cart-samples-root]');
}

function getSamplesWrapper() {
  return document.querySelector('[data-cart-samples]');
}

function getSamplesPopup(popupId) {
  const popupKey = popupId || getSamplesWrapper()?.dataset.popupId || 'samples-popup-main';
  return document.querySelector('[data-samples-popup-id="' + popupKey + '"]');
}

function getFirstSampleThresholdCents() {
  const wrapper = getSamplesWrapper();
  return wrapper ? parseInt(wrapper.dataset.firstSampleThresholdCents || '2500', 10) : 2500;
}

function getSecondSampleThresholdCents() {
  const wrapper = getSamplesWrapper();
  return wrapper ? parseInt(wrapper.dataset.secondSampleThresholdCents || '5000', 10) : 5000;
}

function getEligibleSampleCount(nonSampleSubtotal) {
  const subtotal = typeof nonSampleSubtotal === 'number' ? nonSampleSubtotal : getVisibleSubtotalFromDom();

  if (subtotal >= getSecondSampleThresholdCents()) return 2;
  if (subtotal >= getFirstSampleThresholdCents()) return 1;
  return 0;
}

function getVisibleSubtotalFromDom() {
  const items = document.querySelectorAll('[data-item-final-line-price]');

  if (!items.length) {
    const wrapper = getSamplesWrapper();
    return wrapper ? parseInt(wrapper.dataset.nonSampleSubtotal || '0', 10) : 0;
  }

  return Array.from(items).reduce(function (total, item) {
    return total + parseInt(item.getAttribute('data-item-final-line-price') || '0', 10);
  }, 0);
}

function isSamplesPopupOpen() {
  return document.documentElement.classList.contains('samples-popup-open');
}

function setSamplesSyncing(isSyncing) {
  const wrapper = getSamplesWrapper();
  if (wrapper) {
    wrapper.classList.toggle('cart-samples--syncing', isSyncing);
  }
}

function getCartDrawer() {
  return document.querySelector('#cart-drawer');
}

function getCartItemsElement() {
  return document.querySelector('cart-drawer cart-items') || document.querySelector('cart-items');
}

function lockBackgroundCart() {
  const drawer = getCartDrawer();
  if (drawer) {
    drawer.classList.add('samples-popup-cart-locked');
    drawer.setAttribute('aria-hidden', 'true');
  }
}

function unlockBackgroundCart() {
  const drawer = getCartDrawer();
  if (drawer) {
    drawer.classList.remove('samples-popup-cart-locked');
    drawer.removeAttribute('aria-hidden');
  }
}

function openSamplesPopup(popup) {
  const dialog = popup.querySelector('[data-samples-popup-dialog]');
  const closeButton = popup.querySelector('.samples-popup__close');

  popup.hidden = false;
  popup.setAttribute('aria-hidden', 'false');
  document.documentElement.classList.add('samples-popup-open');
  document.body.classList.add('samples-popup-open');
  lockBackgroundCart();

  if (window.theme && window.theme.a11y && typeof window.theme.a11y.trapFocus === 'function' && dialog) {
    window.theme.a11y.trapFocus(popup, {
      elementToFocus: closeButton || dialog
    });
  } else if (closeButton) {
    closeButton.focus();
  } else if (dialog) {
    dialog.focus();
  }
}

function closeSamplesPopup(popup) {
  draftSelections[popup.dataset.samplesPopupId] = null;
  popup.hidden = true;
  popup.setAttribute('aria-hidden', 'true');
  document.documentElement.classList.remove('samples-popup-open');
  document.body.classList.remove('samples-popup-open');
  unlockBackgroundCart();

  if (window.theme && window.theme.a11y && typeof window.theme.a11y.removeTrapFocus === 'function') {
    window.theme.a11y.removeTrapFocus();
  }

  if (lastPopupTrigger && document.contains(lastPopupTrigger)) {
    lastPopupTrigger.focus();
  }
}

function moveSamplesPopupsToBody() {
  document.querySelectorAll('[data-samples-popup]').forEach(function (popup) {
    const popupId = popup.dataset.samplesPopupId || '';
    const existingPopup =
      popupId && popup.parentNode !== document.body
        ? document.body.querySelector('[data-samples-popup-id="' + popupId + '"]')
        : null;

    if (existingPopup && existingPopup !== popup) {
      existingPopup.remove();
    }

    if (popup.parentNode !== document.body) {
      document.body.appendChild(popup);
    }
  });
}

function getPopupId(popup) {
  return popup.dataset.samplesPopupId;
}

function readSelectedIdsFromWrapper() {
  const wrapper = getSamplesWrapper();
  if (!wrapper) return null;

  return (wrapper.dataset.selectedIds || '')
    .split(',')
    .map(function (id) {
      return id.trim();
    })
    .filter(Boolean);
}

function setWrapperSelectedIds(selectedIds) {
  const wrapper = getSamplesWrapper();
  const normalizedIds = (selectedIds || []).map(String);

  currentSelectedSampleIds = normalizedIds.slice();

  if (wrapper) {
    wrapper.dataset.selectedIds = normalizedIds.join(',');
  }
}

function getCurrentSelectedIds() {
  const wrapperIds = readSelectedIdsFromWrapper();

  if (wrapperIds !== null) {
    currentSelectedSampleIds = wrapperIds.slice();
  }

  return currentSelectedSampleIds.slice();
}

function getSelectedIdsFromPopup() {
  const popup = getSamplesPopup();
  if (!popup) return [];

  return Array.from(popup.querySelectorAll('[data-sample-card][data-selected="true"]')).map(function (card) {
    return String(card.dataset.variantId);
  });
}

function initializeDraftSelections(popup) {
  draftSelections[getPopupId(popup)] = getCurrentSelectedIds();
}

function clearDraftSelections(popup) {
  draftSelections[getPopupId(popup)] = [];
  renderDraftSelections(popup);
}

function toggleDraftSelection(card, popup) {
  const popupId = getPopupId(popup);
  const maxSamples = getEligibleSampleCount();
  const variantId = String(card.dataset.variantId);
  const feedback = popup.querySelector('[data-samples-feedback]');

  if (!Array.isArray(draftSelections[popupId])) {
    draftSelections[popupId] = [];
  }

  const currentSelections = draftSelections[popupId];
  const alreadySelected = currentSelections.includes(variantId);

  if (alreadySelected) {
    draftSelections[popupId] = currentSelections.filter(function (id) {
      return id !== variantId;
    });
  } else {
    if (currentSelections.length >= maxSamples) {
      if (feedback) {
        feedback.textContent =
          "You've selected your " + maxSamples + " sample" + (maxSamples === 1 ? '' : 's') + ". If you’d like to make a change, just unselect one before continuing.";
      }
      return;
    }

    draftSelections[popupId] = currentSelections.concat(variantId);
  }

  renderDraftSelections(popup);
}

function applySelectionStateToPopup(popup, selectedIds) {
  const selectedLookup = new Set((selectedIds || []).map(String));
  const feedback = popup.querySelector('[data-samples-feedback]');

  popup.querySelectorAll('[data-sample-card]').forEach(function (card) {
    const isSelected = selectedLookup.has(String(card.dataset.variantId));
    card.dataset.selected = isSelected ? 'true' : 'false';
    card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    card.classList.toggle('is-selected', isSelected);
  });

  if (feedback) {
    feedback.textContent = selectedIds.length
      ? selectedIds.length + ' sample' + (selectedIds.length === 1 ? '' : 's') + ' selected'
      : '0 samples selected';
  }
}

function setPopupSelectionState(selectedIds) {
  const popup = getSamplesPopup();
  setWrapperSelectedIds(selectedIds);

  if (!popup) return;

  applySelectionStateToPopup(popup, selectedIds);
}

function renderDraftSelections(popup) {
  const popupId = getPopupId(popup);
  const selectedIds = Array.isArray(draftSelections[popupId]) ? draftSelections[popupId] : [];
  applySelectionStateToPopup(popup, selectedIds);
}

function getSampleCardMeta(variantId) {
  const popup = getSamplesPopup();
  if (!popup) return null;

  const card = popup.querySelector('[data-sample-card][data-variant-id="' + String(variantId) + '"]');
  if (!card) return null;

  return {
    variantId: String(variantId),
    vendor: card.dataset.sampleVendor || '',
    title: card.dataset.sampleTitle || '',
    imageUrl: card.dataset.sampleImageUrl || ''
  };
}

function mergeSampleOrder(selectedIds) {
  const normalizedIds = (selectedIds || []).map(String);

  if (!normalizedIds.length) {
    return [];
  }

  if (!lastConfirmedSampleOrder.length) {
    return normalizedIds.slice();
  }

  const selectedLookup = new Set(normalizedIds);
  const preservedIds = lastConfirmedSampleOrder.filter(function (id) {
    return selectedLookup.has(String(id));
  });
  const preservedLookup = new Set(preservedIds.map(String));
  const newIds = normalizedIds.filter(function (id) {
    return !preservedLookup.has(String(id));
  });

  return preservedIds.concat(newIds);
}

function getSortedSelectedSampleMeta(selectedIds) {
  const selectedList = (selectedIds || []).map(String);
  const orderSource = lastConfirmedSampleOrder.length ? lastConfirmedSampleOrder : selectedList;
  const orderMap = new Map(orderSource.map(function (id, index) {
    return [String(id), index];
  }));

  return selectedList
    .map(getSampleCardMeta)
    .filter(Boolean)
    .sort(function (a, b) {
      const aOrder = orderMap.has(a.variantId) ? orderMap.get(a.variantId) : 999;
      const bOrder = orderMap.has(b.variantId) ? orderMap.get(b.variantId) : 999;
      return aOrder - bOrder;
    });
}

function renderSamplesSummary(nonSampleSubtotal, selectedIds) {
  const wrapper = getSamplesWrapper();
  if (!wrapper) return;

  const summary = wrapper.querySelector('.cart-samples-summary');
  if (!summary) return;

  const maxSamples = getEligibleSampleCount(nonSampleSubtotal);
  const thresholdCents = getFirstSampleThresholdCents();
  const qualifies = maxSamples > 0;
  const selectedMeta = getSortedSelectedSampleMeta(selectedIds);
  const popupTitle = getSamplesPopup()?.querySelector('.samples-popup__title');

  wrapper.dataset.nonSampleSubtotal = String(nonSampleSubtotal);
  wrapper.dataset.qualifies = qualifies ? 'true' : 'false';
  wrapper.dataset.selectedIds = (selectedIds || []).map(String).join(',');

  if (popupTitle && qualifies) {
    popupTitle.textContent = 'Select up to ' + maxSamples + ' free sample' + (maxSamples === 1 ? '' : 's');
  }

  if (qualifies) {
    if (selectedMeta.length) {
      summary.innerHTML = `
        <p class="cart-samples-summary__title">
          (${selectedMeta.length} of ${maxSamples} Free sample${maxSamples === 1 ? '' : 's'} selected)
        </p>
        <div class="cart-samples-summary__items">
          ${selectedMeta
            .map(function (sample) {
              const image = sample.imageUrl
                ? `<img class="cart-samples-summary__image" src="${escapeHtml(sample.imageUrl)}" alt="${escapeHtml(
                    sample.title
                  )}">`
                : '';
              const name = (sample.vendor ? sample.vendor + ' ' : '') + sample.title;

              return `
                <div class="cart-samples-summary__item" data-variant-id="${escapeHtml(sample.variantId)}">
                  <div class="cart-samples-summary__image-wrap">
                    ${image}
                  </div>
                  <div class="cart-samples-summary__content">
                    <p class="cart-samples-summary__name">${escapeHtml(name)}</p>
                  </div>
                </div>
              `;
            })
            .join('')}
        </div>
        <button type="button" class="cart-samples-summary__change" data-open-samples-popup>
          Change selection
        </button>
      `;
      return;
    }

    summary.innerHTML = `
      <button type="button" class="cart-samples-summary__cta" data-open-samples-popup>
        Select your free sample${maxSamples === 1 ? '' : 's'}
      </button>
    `;
    return;
  }

  summary.innerHTML = `
    <p class="cart-samples-summary__title">
      You are ${formatMoney(Math.max(0, thresholdCents - nonSampleSubtotal))} away from 1 free sample
    </p>
  `;
}

function buildSampleUpdates(sampleVariantIds, quantity) {
  return (sampleVariantIds || []).reduce(function (updates, variantId) {
    updates[String(variantId)] = quantity;
    return updates;
  }, {});
}

function createPendingSamplesCommit() {
  let resolvePendingCommit;

  pendingSamplesCommit = new Promise(function (resolve) {
    resolvePendingCommit = resolve;
  });

  return function finishPendingSamplesCommit() {
    if (resolvePendingCommit) {
      resolvePendingCommit();
      resolvePendingCommit = null;
    }

    pendingSamplesCommit = null;
  };
}

async function confirmDraftSelections(popup) {
  const popupId = getPopupId(popup);
  const selectedIds = Array.isArray(draftSelections[popupId]) ? draftSelections[popupId] : [];
  const previousSelectedIds = getCurrentSelectedIds();
  const confirmBtn = popup.querySelector('[data-confirm-samples]');

  if (selectedIds.length > getEligibleSampleCount() || isConfirmingSamples) return;

  if (sameIdSet(selectedIds, previousSelectedIds)) {
    closeSamplesPopup(popup);
    return;
  }

  isConfirmingSamples = true;
  const finishPendingSamplesCommit = createPendingSamplesCommit();
  setSamplesSyncing(true);
  popup.classList.add('is-loading');
  if (confirmBtn) {
    confirmBtn.disabled = true;
  }

  lastConfirmedSampleOrder = mergeSampleOrder(selectedIds);
  setPopupSelectionState(selectedIds);
  renderSamplesSummary(getVisibleSubtotalFromDom(), selectedIds);
  closeSamplesPopup(popup);

  try {
    const updates = buildSampleUpdates(
      previousSelectedIds.filter(function (variantId) {
        return !selectedIds.includes(String(variantId));
      }),
      0
    );

    if (Object.keys(updates).length) {
      await postCartUpdate({ updates: updates });
    }

    const toAdd = selectedIds.filter(function (variantId) {
      return !previousSelectedIds.includes(String(variantId));
    });

    if (toAdd.length) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: toAdd.map(function (variantId) {
            return {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _free_sample: 'true'
              }
            };
          })
        })
      });

      if (!response.ok) {
        throw new Error('Sample add failed: ' + response.status);
      }
    }

    updateUiFromCart(await waitForExpectedSamplesInCart(selectedIds));
    await refreshSamplesMarkupFromApi();
    requestNativeCartRefresh();
    scheduleSamplesSync(CART_SAMPLES_SYNC_DELAY);
  } catch (error) {
    console.error(error);
    setPopupSelectionState(previousSelectedIds);
    renderSamplesSummary(getVisibleSubtotalFromDom(), previousSelectedIds);
  } finally {
    popup.classList.remove('is-loading');
    if (confirmBtn) {
      confirmBtn.disabled = false;
    }
    setSamplesSyncing(false);
    isConfirmingSamples = false;
    finishPendingSamplesCommit();
  }
}

function getSampleItems(cart) {
  return cart.items.filter(function (item) {
    return isSampleItem(item);
  });
}

function isSampleItem(item) {
  const hasSampleProperty = item.properties && item.properties._free_sample === 'true';
  const hasSampleSku = String(item.sku || '').trim().toLowerCase() === 'freesample';

  return hasSampleProperty || hasSampleSku;
}

function getNonSampleSubtotalFromCart(cart) {
  return cart.items.reduce(function (total, item) {
    if (isSampleItem(item)) {
      return total;
    }

    return total + item.final_line_price;
  }, 0);
}

async function enforceSampleEligibilityBeforeCheckout() {
  const cart = await fetchCart();
  const sampleItems = getSampleItems(cart);
  const eligibleSampleCount = getEligibleSampleCount(getNonSampleSubtotalFromCart(cart));

  if (sampleItems.length <= eligibleSampleCount) {
    updateUiFromCart(cart);
    return;
  }

  const updates = {};
  const itemsToRemove = sampleItems.slice(eligibleSampleCount);

  itemsToRemove.forEach(function (item) {
    updates[item.key] = 0;
  });

  if (Object.keys(updates).length) {
    const updatedCart = await postCartUpdate({ updates: updates });
    updateUiFromCart(updatedCart);
    await refreshSamplesMarkupFromApi();
    requestNativeCartRefresh();
  }
}

function updateUiFromCart(cart) {
  const selectedIds = getSampleItems(cart).map(function (item) {
    return String(item.variant_id);
  });

  lastConfirmedSampleOrder = mergeSampleOrder(selectedIds);
  setPopupSelectionState(selectedIds);
  renderSamplesSummary(getNonSampleSubtotalFromCart(cart), selectedIds);
}

async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: {
      Accept: 'application/json'
    },
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error('Cart fetch failed: ' + response.status);
  }

  return response.json();
}

async function postCartUpdate(payload) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error('Cart update failed: ' + response.status);
  }

  return response.json();
}

function wait(ms) {
  return new Promise(function (resolve) {
    window.setTimeout(resolve, ms);
  });
}

async function removeAllSampleItemsFromCart(selectedIds) {
  if (!selectedIds.length || isRemovingSamplesForThreshold || isConfirmingSamples) return;

  isRemovingSamplesForThreshold = true;
  const finishPendingSamplesCommit = createPendingSamplesCommit();
  setSamplesSyncing(true);

  try {
    const updates = buildSampleUpdates(selectedIds, 0);

    if (Object.keys(updates).length) {
      const updatedCart = await postCartUpdate({ updates: updates });
      updateUiFromCart(updatedCart);
      await refreshSamplesMarkupFromApi();
      requestNativeCartRefresh();
      scheduleSamplesSync(CART_SAMPLES_SYNC_DELAY);
    }
  } catch (error) {
    console.error(error);
    setPopupSelectionState(selectedIds);
    renderSamplesSummary(getVisibleSubtotalFromDom(), selectedIds);
  } finally {
    setSamplesSyncing(false);
    isRemovingSamplesForThreshold = false;
    finishPendingSamplesCommit();
  }
}

function syncSamplesUiFromDom() {
  if (isConfirmingSamples) return;

  const wrapper = getSamplesWrapper();
  if (!wrapper) return;

  const nonSampleSubtotal = getVisibleSubtotalFromDom();
  const selectedIds = getCurrentSelectedIds();
  const eligibleSampleCount = getEligibleSampleCount(nonSampleSubtotal);

  if (!lastConfirmedSampleOrder.length && selectedIds.length) {
    lastConfirmedSampleOrder = mergeSampleOrder(selectedIds);
  }

  renderSamplesSummary(nonSampleSubtotal, selectedIds);

  if (selectedIds.length > eligibleSampleCount) {
    const popup = getSamplesPopup();
    if (popup && isSamplesPopupOpen()) {
      closeSamplesPopup(popup);
    }

    const eligibleSelectedIds = selectedIds.slice(0, eligibleSampleCount);
    const sampleIdsToRemove = selectedIds.slice(eligibleSampleCount);
    setPopupSelectionState(eligibleSelectedIds);
    renderSamplesSummary(nonSampleSubtotal, eligibleSelectedIds);
    removeAllSampleItemsFromCart(sampleIdsToRemove);
  }
}

function scheduleSamplesSync(delay) {
  clearTimeout(samplesSyncTimeout);
  samplesSyncTimeout = setTimeout(syncSamplesUiFromDom, typeof delay === 'number' ? delay : 0);
}

function requestNativeCartRefresh() {
  const cartItems = getCartItemsElement();

  if (cartItems && typeof cartItems.getCart === 'function') {
    cartItems.getCart();
  }
}

function shouldHandleCartRemoval(removeBtn) {
  return Boolean(removeBtn?.dataset.id);
}

function setCartItemRemovalState(removeBtn, isLoading) {
  const cartItems = getCartItemsElement();
  const item = removeBtn.closest('[data-item]');

  if (cartItems) {
    cartItems.classList.toggle('is-loading', isLoading);
  }

  if (item) {
    item.classList.toggle('is-loading', isLoading);

    if (!isLoading) {
      item.classList.remove('is-removed');
    }
  }

  removeBtn.toggleAttribute('disabled', isLoading);
}

async function removeCartItem(removeBtn) {
  if (isRemovingCartItem) return;

  const itemKey = removeBtn.dataset.id;
  if (!itemKey) return;

  isRemovingCartItem = true;
  setCartItemRemovalState(removeBtn, true);

  try {
    const response = await fetch(
      (window.theme && window.theme.routes && window.theme.routes.cart_change_url) || '/cart/change.js',
      {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0
      })
      }
    );

    if (!response.ok) {
      throw new Error('Cart item removal failed: ' + response.status);
    }

    requestNativeCartRefresh();
    scheduleSamplesSync(CART_SAMPLES_SYNC_DELAY);
  } catch (error) {
    console.error(error);
  } finally {
    window.setTimeout(function () {
      setCartItemRemovalState(removeBtn, false);
      isRemovingCartItem = false;
    }, 250);
  }
}

async function refreshSamplesMarkupFromApi() {
  const root = getSamplesRoot();
  if (!root) return;

  const response = await fetch('/cart?section_id=api-cart-items', {
    headers: {
      Accept: 'text/html'
    },
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error('Samples section fetch failed: ' + response.status);
  }

  const html = await response.text();
  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const nextRoot = documentFragment.querySelector('[data-api-cart-samples]');

  if (!nextRoot) return;

  const nextSamplesRoot = nextRoot.querySelector('[data-cart-samples-root]');
  root.innerHTML = nextSamplesRoot ? nextSamplesRoot.innerHTML : nextRoot.innerHTML;
  moveSamplesPopupsToBody();

  const selectedIds = getCurrentSelectedIds();
  if (selectedIds.length) {
    lastConfirmedSampleOrder = mergeSampleOrder(selectedIds);
  }
  setPopupSelectionState(selectedIds);
  renderSamplesSummary(getVisibleSubtotalFromDom(), selectedIds);
}

function prepareCartInputsForCheckoutSubmission(form) {
  if (form.querySelector('[data-cart-quantity-input], [data-sample-update-input]')) {
    return;
  }

  const existingProxy = form.querySelector('[data-cart-submit-proxy]');
  if (existingProxy) {
    existingProxy.remove();
  }

  const cartRoot =
    form.closest('cart-drawer')?.querySelector('cart-items') ||
    document.querySelector('[data-cart-page] cart-items') ||
    document.querySelector('cart-items');

  if (!cartRoot) return;

  const quantityInputs = cartRoot.querySelectorAll('[data-cart-quantity-input], [data-sample-update-input]');
  if (!quantityInputs.length) return;

  const proxy = document.createElement('div');
  proxy.hidden = true;
  proxy.setAttribute('data-cart-submit-proxy', '');

  quantityInputs.forEach(function (input) {
    const clonedInput = document.createElement('input');
    clonedInput.type = 'hidden';
    clonedInput.name = 'updates[]';
    clonedInput.value = input.value || '0';
    proxy.appendChild(clonedInput);
  });

  form.appendChild(proxy);
}

async function waitForExpectedSamplesInCart(expectedSelectedIds) {
  const expected = (expectedSelectedIds || []).map(String).slice().sort();
  let attempts = 0;

  while (attempts < 5) {
    const cart = await fetchCart();
    const actual = getSampleItems(cart)
      .map(function (item) {
        return String(item.variant_id);
      })
      .sort();

    if (sameIdSet(actual, expected)) {
      return cart;
    }

    attempts += 1;
    await wait(150);
  }

  return fetchCart();
}

function sameIdSet(a, b) {
  if (a.length !== b.length) return false;

  const aSorted = a.map(String).slice().sort();
  const bSorted = b.map(String).slice().sort();

  return aSorted.every(function (id, index) {
    return id === bSorted[index];
  });
}

function formatMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    const moneyFormat =
      (window.theme && (window.theme.moneyFormat || window.theme.moneyWithCurrencyFormat)) ||
      window.Shopify.money_format ||
      '${{amount}}';

    return window.Shopify.formatMoney(cents, moneyFormat);
  }

  return '$' + ((cents || 0) / 100).toFixed(2);
}

function escapeHtml(str) {
  return String(str || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}